import {
  Br,
  Cashdraw,
  Cut,
  Image as Imag,
  Line,
  Printer,
  QRCode,
  render,
  Row,
  Text,
} from "react-thermal-printer";
import { transforms } from "@react-thermal-printer/image";

/* ================================================================
 * CONSTANTES
 * ================================================================ */

const STORAGE_KEY = "impresoras_termicas_configuracion_v1";

const DEFAULT_BAUD_RATE = 9600;

const DEFAULT_TIMEOUT = 5000;

const DEFAULT_REINTENTOS = 2;

const TIPOS_CONEXION = {
  USB: "USB",
  SERIAL: "SERIAL",
  BLUETOOTH: "BLUETOOTH",
};

const TAMANOS_PAPEL = {
  58: {
    mm: 58,
    width: 32,
    nombre: "58 mm",
  },

  80: {
    mm: 80,
    width: 48,
    nombre: "80 mm",
  },
};

/*
 * UUIDs comunes para impresoras Bluetooth BLE.
 *
 * NO todas las impresoras utilizan estos UUID.
 *
 * Si tu impresora Bluetooth utiliza otros UUID puedes
 * configurarlos en:
 *
 *   bluetooth.serviceUUID
 *   bluetooth.characteristicUUID
 */
const BLUETOOTH_DEFAULTS = {
  serviceUUID: "0000ff00-0000-1000-8000-00805f9b34fb",
  characteristicUUID: "0000ff02-0000-1000-8000-00805f9b34fb",
};

/*
 * Servicios BLE que suelen utilizar algunos dispositivos
 * de impresión térmica.
 *
 * IMPORTANTE:
 * No todas las impresoras utilizan estos UUID.
 *
 * El manager intentará primero los UUID configurados
 * y posteriormente estos UUID candidatos.
 */
const BLUETOOTH_SERVICE_UUIDS = [
  "0000ff00-0000-1000-8000-00805f9b34fb",
  "0000ffe0-0000-1000-8000-00805f9b34fb",
  "0000ffe5-0000-1000-8000-00805f9b34fb",
  "0000fee7-0000-1000-8000-00805f9b34fb",
  "000018f0-0000-1000-8000-00805f9b34fb",
];

/*
 * Algunas impresoras utilizan UUID de característica
 * diferentes al 0000ff02.
 *
 * Esta lista sirve como candidatos.
 */
const BLUETOOTH_CHARACTERISTIC_UUIDS = [
  "0000ff02-0000-1000-8000-00805f9b34fb",
  "0000ffe1-0000-1000-8000-00805f9b34fb",
  "0000ffe9-0000-1000-8000-00805f9b34fb",
  "0000ff01-0000-1000-8000-00805f9b34fb",
];

/* ================================================================
 * UTILIDADES
 * ================================================================ */

function generarId() {
  return (
    "printer_" +
    Date.now().toString(36) +
    "_" +
    Math.random().toString(36).substring(2, 10)
  );
}

function convertirUint8Array(data) {
  if (data instanceof Uint8Array) {
    return data;
  }

  if (data instanceof ArrayBuffer) {
    return new Uint8Array(data);
  }

  if (ArrayBuffer.isView(data)) {
    return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  }

  throw new Error("Los datos de impresión deben ser Uint8Array o ArrayBuffer.");
}

function obtenerNombreSeguro(device, fallback = "Impresora") {
  if (!device) {
    return fallback;
  }

  return device.productName || device.name || fallback;
}

function normalizarTipoConexion(tipo) {
  if (!tipo) {
    return null;
  }

  return String(tipo).toUpperCase();
}

function dormir(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/* ================================================================
 * CLASE PRINCIPAL
 * ================================================================ */

class ImpresoraManager {
  constructor() {
    /*
     * ============================================================
     * ESTADO GENERAL
     * ============================================================
     */

    this.impresoraConectada = false;

    this.impresoraActual = null;

    this.tipoConexionActual = null;

    this.estado = "desconectada";

    /*
     * Estados posibles:
     *
     *   inicializando
     *   desconectada
     *   conectando
     *   conectada
     *   imprimiendo
     *   error
     *   reconectando
     */

    this.errorActual = null;

    /*
     * ============================================================
     * DISPOSITIVOS NATIVOS
     * ============================================================
     */

    this.usbDevice = null;

    this.serialPort = null;

    this.bluetoothDevice = null;

    this.bluetoothServer = null;

    this.bluetoothCharacteristic = null;

    /*
     * ============================================================
     * CONFIGURACIÓN
     * ============================================================
     */

    this.configuracion = {
      tipoConexion: null,

      nombre: null,

      anchoPapel: 80,

      width: 48,

      baudRate: DEFAULT_BAUD_RATE,

      timeout: DEFAULT_TIMEOUT,

      reintentos: DEFAULT_REINTENTOS,

      printerType: "epson",

      encoder: null,

      bluetooth: {
        serviceUUID: BLUETOOTH_DEFAULTS.serviceUUID,

        characteristicUUID: BLUETOOTH_DEFAULTS.characteristicUUID,
      },
    };

    /*
     * ============================================================
     * IMPRESORAS GUARDADAS
     * ============================================================
     */

    this.impresorasGuardadas = [];

    /*
     * ============================================================
     * COLA DE IMPRESIÓN
     * ============================================================
     */

    this.colaImpresion = [];

    this.imprimiendo = false;

    this.trabajoImprimiendo = null;

    this.historialImpresion = [];

    this.maxHistorialImpresion = 20;

    this.idTrabajoImpresion = 0;

    /*
     * ============================================================
     * EVENTOS
     * ============================================================
     */

    this.listeners = {
      estado: new Set(),
      conexion: new Set(),
      desconexion: new Set(),
      error: new Set(),
      impresora: new Set(),
      cola: new Set(),
      impresion: new Set(),
    };

    /*
     * ============================================================
     * INICIALIZACIÓN
     * ============================================================
     */

    this.inicializado = false;

    this.inicializandoPromise = null;

    /*
     * ============================================================
     * CONTROL DE RECONEXIÓN
     * ============================================================
     */

    this.reconectando = false;

    this.detenerReconectar = false;

    this.intervaloReconectar = null;

    this.intervaloMonitoreo = null;

    this.ultimoIntentoReconectar = 0;

    /*
     * ============================================================
     * CALLBACK PARA PEDIR OTRA IMPRESORA
     * ============================================================
     */

    this.onSeleccionarOtraImpresora = null;

    /*
     * ============================================================
     * BIND DE EVENTOS
     * ============================================================
     */

    this._onUsbConnect = this._onUsbConnect.bind(this);

    this._onUsbDisconnect = this._onUsbDisconnect.bind(this);

    this._onSerialConnect = this._onSerialConnect.bind(this);

    this._onSerialDisconnect = this._onSerialDisconnect.bind(this);

    this._onBluetoothGattServerDisconnected =
      this._onBluetoothGattServerDisconnected.bind(this);

    /*
     * Cargar configuración almacenada.
     */

    this.cargarConfiguracion();
  }

  /* ==============================================================
   * INICIALIZAR
   * ============================================================== */

  async inicializar() {
    if (this.inicializado) {
      return this.obtenerEstado();
    }

    if (this.inicializandoPromise) {
      return this.inicializandoPromise;
    }

    this.inicializandoPromise = this._inicializarInterno();

    try {
      return await this.inicializandoPromise;
    } finally {
      this.inicializandoPromise = null;
    }
  }

  async _inicializarInterno() {
    this.cambiarEstado("inicializando");

    this.detenerReconectar = false;

    this.instalarListenersNavegador();

    /*
     * Recuperar dispositivos autorizados.
     */

    await this.detectarDispositivosAutorizados();

    this.inicializado = true;

    /*
     * Intentar reconectar la última impresora.
     */

    if (this.impresoraActual) {
      try {
        await this.reconectar();
      } catch (error) {
        console.warn(
          "[ImpresoraManager] No fue posible reconectar automáticamente:",
          error,
        );

        this.cambiarEstado("desconectada");
      }
    } else {
      this.cambiarEstado("desconectada");
    }

    return this.obtenerEstado();
  }

  /* ==============================================================
   * CONFIGURACIÓN
   * ============================================================== */

  configurar(opciones = {}) {
    if (opciones.anchoPapel) {
      this.configurarTamano(opciones.anchoPapel);
    }

    if (opciones.tipoConexion) {
      this.configuracion.tipoConexion = normalizarTipoConexion(
        opciones.tipoConexion,
      );
    }

    if (opciones.nombre !== undefined) {
      this.configuracion.nombre = opciones.nombre;
    }

    if (opciones.baudRate) {
      this.configuracion.baudRate = opciones.baudRate;
    }

    if (opciones.timeout) {
      this.configuracion.timeout = opciones.timeout;
    }

    if (opciones.reintentos !== undefined) {
      this.configuracion.reintentos = Math.max(0, Number(opciones.reintentos));
    }

    if (opciones.printerType) {
      this.configuracion.printerType = opciones.printerType;
    }

    if (opciones.encoder) {
      this.configuracion.encoder = opciones.encoder;
    }

    if (opciones.bluetooth) {
      this.configuracion.bluetooth = {
        ...this.configuracion.bluetooth,
        ...opciones.bluetooth,
      };
    }

    this.guardarConfiguracion();

    return this.configuracion;
  }

  emitirCambioCola() {
    this.emitir("cola", {
      cola: this.obtenerColaPendiente(),

      historial: this.obtenerHistorialImpresion(),

      actual: this.trabajoImprimiendo
        ? {
            ...this.trabajoImprimiendo,
            receipt: undefined,
          }
        : null,

      pendientes: this.colaImpresion.filter(
        (tarea) => tarea.estado === "pendiente" || tarea.estado === "error",
      ).length,

      imprimiendo: this.trabajoImprimiendo !== null,

      total: this.colaImpresion.length,

      impresoraConectada: this.impresoraConectada,

      impresoraActual: this.impresoraActual,
    });
  }

  cancelarTrabajoImpresion(id) {
    const index = this.colaImpresion.findIndex((tarea) => tarea.id === id);

    if (index === -1) {
      return false;
    }

    const tarea = this.colaImpresion[index];

    /*
     * No permitimos cancelar
     * una impresión que ya comenzó.
     */
    if (tarea.estado === "imprimiendo") {
      return false;
    }

    tarea.estado = "cancelado";
    tarea.fechaFin = Date.now();

    this.colaImpresion.splice(index, 1);

    this.historialImpresion.unshift({
      ...tarea,
    });

    this.emitirCambioCola();

    return true;
  }

  async reintentarTrabajoImpresion(id) {
    const tarea = this.colaImpresion.find((item) => item.id === id);

    if (!tarea) {
      return false;
    }

    if (tarea.estado !== "error") {
      return false;
    }

    tarea.estado = "pendiente";
    tarea.error = null;

    this.emitirCambioCola();

    await this.procesarCola();

    return true;
  }

  async obtenerServiciosBluetooth(server) {
    if (!server) {
      throw new Error("No existe servidor GATT Bluetooth.");
    }

    const servicios = [];

    /*
     * Primero intentamos obtener los servicios primarios
     * que el navegador nos haya permitido utilizar.
     */
    try {
      const primaryServices = await server.getPrimaryServices();

      for (const service of primaryServices) {
        servicios.push(service);
      }
    } catch (error) {
      console.warn(
        "[ImpresoraManager] No se pudieron obtener todos los servicios Bluetooth:",
        error,
      );
    }

    return servicios;
  }

  async buscarServicioBluetooth(server, serviceUUID = null) {
    if (!server) {
      throw new Error("No existe servidor GATT Bluetooth.");
    }

    /*
     * Si tenemos un UUID específico, intentarlo primero.
     */
    if (serviceUUID) {
      try {
        const service = await server.getPrimaryService(serviceUUID);

        console.log(
          "[ImpresoraManager] Servicio Bluetooth encontrado:",
          service.uuid,
        );

        return service;
      } catch (error) {
        console.warn(
          "[ImpresoraManager] El servicio configurado no existe:",
          serviceUUID,
        );
      }
    }

    /*
     * Intentar servicios candidatos.
     */
    for (const uuid of BLUETOOTH_SERVICE_UUIDS) {
      try {
        const service = await server.getPrimaryService(uuid);

        console.log(
          "[ImpresoraManager] Servicio Bluetooth encontrado automáticamente:",
          uuid,
        );

        return service;
      } catch (_) {
        /*
         * Continuar con el siguiente UUID.
         */
      }
    }

    /*
     * Intentar obtener servicios autorizados.
     */
    const servicios = await this.obtenerServiciosBluetooth(server);

    if (servicios.length > 0) {
      console.log(
        "[ImpresoraManager] Servicios Bluetooth disponibles:",
        servicios.map((service) => service.uuid),
      );

      /*
       * Buscar uno que tenga una característica de escritura.
       */
      for (const service of servicios) {
        try {
          const characteristics = await service.getCharacteristics();

          const writable = characteristics.find(
            (characteristic) =>
              characteristic.properties?.write ||
              characteristic.properties?.writeWithoutResponse,
          );

          if (writable) {
            console.log(
              "[ImpresoraManager] Servicio de impresión detectado:",
              service.uuid,
            );

            return service;
          }
        } catch (error) {
          console.warn(
            "[ImpresoraManager] No se pudieron leer características del servicio:",
            service.uuid,
            error,
          );
        }
      }
    }

    throw new Error(
      "No se encontró ningún servicio Bluetooth compatible con impresión. " +
        "La impresora puede utilizar un UUID BLE diferente o Bluetooth clásico (SPP).",
    );
  }

  async buscarCaracteristicaBluetooth(service, characteristicUUID = null) {
    if (!service) {
      throw new Error("No existe servicio Bluetooth.");
    }

    /*
     * ============================================================
     * INTENTAR CARACTERÍSTICA CONFIGURADA
     * ============================================================
     */

    if (characteristicUUID) {
      try {
        const characteristic =
          await service.getCharacteristic(characteristicUUID);

        if (
          characteristic.properties?.write ||
          characteristic.properties?.writeWithoutResponse
        ) {
          console.log(
            "[ImpresoraManager] Característica Bluetooth encontrada:",
            characteristic.uuid,
          );

          return characteristic;
        }
      } catch (error) {
        console.warn(
          "[ImpresoraManager] La característica configurada no existe:",
          characteristicUUID,
        );
      }
    }

    /*
     * ============================================================
     * INTENTAR UUID CONOCIDOS
     * ============================================================
     */

    for (const uuid of BLUETOOTH_CHARACTERISTIC_UUIDS) {
      try {
        const characteristic = await service.getCharacteristic(uuid);

        if (
          characteristic.properties?.write ||
          characteristic.properties?.writeWithoutResponse
        ) {
          console.log(
            "[ImpresoraManager] Característica Bluetooth encontrada automáticamente:",
            uuid,
          );

          return characteristic;
        }
      } catch (_) {
        /*
         * Continuar con la siguiente.
         */
      }
    }

    /*
     * ============================================================
     * INSPECCIONAR TODAS LAS CARACTERÍSTICAS DEL SERVICIO
     * ============================================================
     */

    try {
      const characteristics = await service.getCharacteristics();

      console.log(
        "[ImpresoraManager] Características disponibles:",
        characteristics.map((characteristic) => ({
          uuid: characteristic.uuid,
          properties: characteristic.properties,
        })),
      );

      const writable = characteristics.find(
        (characteristic) =>
          characteristic.properties?.write ||
          characteristic.properties?.writeWithoutResponse,
      );

      if (writable) {
        console.log(
          "[ImpresoraManager] Característica de escritura detectada:",
          writable.uuid,
        );

        return writable;
      }
    } catch (error) {
      console.warn(
        "[ImpresoraManager] No se pudieron obtener las características:",
        error,
      );
    }

    throw new Error(
      `El servicio Bluetooth ${service.uuid} no tiene ninguna característica de escritura.`,
    );
  }

  /* ==============================================================
   * TAMAÑO PAPEL
   * ============================================================== */

  configurarTamano(tamano) {
    let configuracionTamano;

    if (typeof tamano === "string" && tamano.includes("58")) {
      configuracionTamano = TAMANOS_PAPEL[58];
    } else if (typeof tamano === "string" && tamano.includes("80")) {
      configuracionTamano = TAMANOS_PAPEL[80];
    } else {
      configuracionTamano = TAMANOS_PAPEL[Number(tamano)] || TAMANOS_PAPEL[80];
    }

    this.configuracion.anchoPapel = configuracionTamano.mm;

    this.configuracion.width = configuracionTamano.width;

    this.guardarConfiguracion();

    return {
      ...configuracionTamano,
    };
  }

  obtenerTamanosPapel() {
    return Object.values(TAMANOS_PAPEL).map((item) => ({
      ...item,
    }));
  }

  obtenerWidth() {
    return this.configuracion.width;
  }

  /* ==============================================================
   * CONFIGURACIÓN PERSISTENTE
   * ============================================================== */

  cargarConfiguracion() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);

      if (!raw) {
        return;
      }

      const data = JSON.parse(raw);

      if (data.configuracion) {
        this.configuracion = {
          ...this.configuracion,
          ...data.configuracion,
          bluetooth: {
            ...this.configuracion.bluetooth,
            ...(data.configuracion.bluetooth || {}),
          },
        };
      }

      if (Array.isArray(data.impresorasGuardadas)) {
        this.impresorasGuardadas = data.impresorasGuardadas;
      }

      if (data.impresoraActual) {
        this.impresoraActual = data.impresoraActual;
      }

      if (data.tipoConexionActual) {
        this.tipoConexionActual = data.tipoConexionActual;
      }
    } catch (error) {
      console.error("[ImpresoraManager] Error cargando configuración:", error);
    }
  }

  guardarConfiguracion() {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          configuracion: this.configuracion,

          impresorasGuardadas: this.impresorasGuardadas,

          impresoraActual: this.impresoraActual,

          tipoConexionActual: this.tipoConexionActual,
        }),
      );
    } catch (error) {
      console.error("[ImpresoraManager] Error guardando configuración:", error);
    }
  }

  /* ==============================================================
   * DETECTAR DISPOSITIVOS AUTORIZADOS
   * ============================================================== */

  async detectarDispositivosAutorizados() {
    /*
     * ------------------------------------------------------------
     * USB
     * ------------------------------------------------------------
     */

    if ("usb" in navigator && navigator.usb) {
      try {
        const dispositivosUSB = await navigator.usb.getDevices();

        for (const device of dispositivosUSB) {
          this.registrarDispositivoUSB(device, false);
        }
      } catch (error) {
        console.warn(
          "[ImpresoraManager] No se pudieron obtener dispositivos USB:",
          error,
        );
      }
    }

    /*
     * ------------------------------------------------------------
     * SERIAL
     * ------------------------------------------------------------
     */

    if ("serial" in navigator && navigator.serial) {
      try {
        const puertos = await navigator.serial.getPorts();

        for (const port of puertos) {
          this.registrarPuertoSerial(port, false);
        }
      } catch (error) {
        console.warn(
          "[ImpresoraManager] No se pudieron obtener puertos Serial:",
          error,
        );
      }
    }

    /*
     * ------------------------------------------------------------
     * BLUETOOTH
     * ------------------------------------------------------------
     *
     * getDevices() existe solamente en navegadores
     * que soportan esta funcionalidad.
     */

    if (
      navigator.bluetooth &&
      typeof navigator.bluetooth.getDevices === "function"
    ) {
      try {
        const dispositivosBluetooth = await navigator.bluetooth.getDevices();

        for (const device of dispositivosBluetooth) {
          this.registrarDispositivoBluetooth(device, false);
        }
      } catch (error) {
        console.warn(
          "[ImpresoraManager] No se pudieron obtener dispositivos Bluetooth:",
          error,
        );
      }
    }

    return this.listarImpresoras();
  }

  /* ==============================================================
   * LISTAR IMPRESORAS
   * ============================================================== */

  listarImpresoras() {
    return this.impresorasGuardadas.map((impresora) => ({
      ...impresora,
    }));
  }

  /* ==============================================================
   * REGISTRAR USB
   * ============================================================== */

  registrarDispositivoUSB(device, guardar = true) {
    if (!device) {
      return null;
    }

    const id = `usb_${device.vendorId}_${device.productId}`;

    let impresora = this.impresorasGuardadas.find((item) => item.id === id);

    if (!impresora) {
      impresora = {
        id,

        tipoConexion: TIPOS_CONEXION.USB,

        nombre: obtenerNombreSeguro(
          device,
          `USB ${device.vendorId}:${device.productId}`,
        ),

        vendorId: device.vendorId,

        productId: device.productId,

        anchoPapel: this.configuracion.anchoPapel,

        width: this.configuracion.width,

        creada: new Date().toISOString(),

        ultimaConexion: null,
      };

      this.impresorasGuardadas.push(impresora);
    } else {
      impresora.nombre = obtenerNombreSeguro(device, impresora.nombre);

      impresora.vendorId = device.vendorId;

      impresora.productId = device.productId;
    }

    if (guardar) {
      this.guardarConfiguracion();
    }

    return impresora;
  }

  /* ==============================================================
   * REGISTRAR SERIAL
   * ============================================================== */

  registrarPuertoSerial(port, guardar = true) {
    if (!port) {
      return null;
    }

    const info = port.getInfo ? port.getInfo() : {};

    const usbVendorId = info.usbVendorId || "";

    const usbProductId = info.usbProductId || "";

    const id = `serial_${usbVendorId}_${usbProductId}_${this.impresorasGuardadas.length}`;

    /*
     * Intentamos identificar por VID/PID.
     */

    let impresora = this.impresorasGuardadas.find(
      (item) =>
        item.tipoConexion === TIPOS_CONEXION.SERIAL &&
        item.usbVendorId === usbVendorId &&
        item.usbProductId === usbProductId,
    );

    if (!impresora) {
      impresora = {
        id,

        tipoConexion: TIPOS_CONEXION.SERIAL,

        nombre: `Puerto Serial ${usbVendorId || ""}`,

        usbVendorId,

        usbProductId,

        baudRate: this.configuracion.baudRate,

        anchoPapel: this.configuracion.anchoPapel,

        width: this.configuracion.width,

        creada: new Date().toISOString(),

        ultimaConexion: null,
      };

      this.impresorasGuardadas.push(impresora);
    }

    if (guardar) {
      this.guardarConfiguracion();
    }

    return impresora;
  }

  /* ==============================================================
   * REGISTRAR BLUETOOTH
   * ============================================================== */

  registrarDispositivoBluetooth(device, guardar = true) {
    if (!device) {
      return null;
    }

    const id = `bluetooth_${device.id}`;

    let impresora = this.impresorasGuardadas.find((item) => item.id === id);

    if (!impresora) {
      impresora = {
        id,

        tipoConexion: TIPOS_CONEXION.BLUETOOTH,

        nombre: device.name || "Impresora Bluetooth",

        bluetoothId: device.id,

        anchoPapel: this.configuracion.anchoPapel,

        width: this.configuracion.width,

        bluetooth: {
          serviceUUID: this.configuracion.bluetooth.serviceUUID,

          characteristicUUID: this.configuracion.bluetooth.characteristicUUID,
        },

        creada: new Date().toISOString(),

        ultimaConexion: null,
      };

      this.impresorasGuardadas.push(impresora);
    } else {
      impresora.nombre = device.name || impresora.nombre;
    }

    if (guardar) {
      this.guardarConfiguracion();
    }

    return impresora;
  }

  /* ==============================================================
   * CONECTAR
   * ============================================================== */

  async conectar(opciones = {}) {
    /*
     * Permite:
     *
     *   conectar()
     *
     *   conectar({
     *      tipoConexion: "USB"
     *   })
     *
     *   conectar({
     *      tipoConexion: "SERIAL"
     *   })
     *
     *   conectar({
     *      tipoConexion: "BLUETOOTH"
     *   })
     *
     *   conectar({
     *      impresora: objetoImpresora
     *   })
     */

    await this.inicializarSinReconectar();

    this.detenerReconectar = true;

    if (opciones.impresora) {
      this.impresoraActual = opciones.impresora;

      this.configuracion.tipoConexion = opciones.impresora.tipoConexion;
    }

    if (opciones.tipoConexion) {
      this.configuracion.tipoConexion = normalizarTipoConexion(
        opciones.tipoConexion,
      );
    }

    if (opciones.anchoPapel) {
      this.configurarTamano(opciones.anchoPapel);
    }

    const tipo = normalizarTipoConexion(this.configuracion.tipoConexion);

    this.cambiarEstado("conectando");

    this.errorActual = null;

    try {
      let resultado;

      switch (tipo) {
        case TIPOS_CONEXION.USB:
          resultado = await this.conectarUSB(opciones);
          break;

        case TIPOS_CONEXION.SERIAL:
          resultado = await this.conectarSerial(opciones);
          break;

        case TIPOS_CONEXION.BLUETOOTH:
          resultado = await this.conectarBluetooth(opciones);
          break;

        default:
          throw new Error(
            "Debe especificar un tipo de conexión: USB, SERIAL o BLUETOOTH.",
          );
      }

      this.detenerReconectar = false;

      return resultado;
    } catch (error) {
      this.errorActual = this.normalizarError(error);

      this.cambiarEstado("error");

      this.emitir("error", this.errorActual);

      throw error;
    }
  }

  /* ==============================================================
   * INICIALIZAR SIN INTENTAR RECONEXIÓN
   * ============================================================== */

  async inicializarSinReconectar() {
    if (this.inicializado) {
      return;
    }

    this.instalarListenersNavegador();

    await this.detectarDispositivosAutorizados();

    this.inicializado = true;
  }

  /* ==============================================================
   * CONECTAR USB
   * ============================================================== */

  async conectarUSB(opciones = {}) {
    if (!("usb" in navigator)) {
      throw new Error("WebUSB no está disponible en este navegador.");
    }

    let device = opciones.device || null;

    if (!opciones.forzarNuevo) {
      device = this.usbDevice;

      if (!device) {
        const dispositivos = await navigator.usb.getDevices();

        if (dispositivos.length > 0) {
          device = this.buscarUSBCompatible(dispositivos);
        }
      }
    }

    if (!device) {
      device = await navigator.usb.requestDevice({
        filters: [],
      });
    }

    this.usbDevice = device;

    /*
     * Abrir dispositivo.
     */

    if (!device.opened) {
      await device.open();
    }

    /*
     * Seleccionar configuración.
     */

    if (!device.configuration) {
      await device.selectConfiguration(1);
    }

    /*
     * Buscar interfaz de impresión.
     */

    const interfaz = this.buscarInterfazUSB(device);

    if (!interfaz) {
      throw new Error(
        "No se encontró una interfaz USB compatible para impresión.",
      );
    }

    /*
     * Reclamar interfaz.
     */

    try {
      await device.claimInterface(interfaz.interfaceNumber);
    } catch (error) {
      /*
       * Algunos dispositivos pueden
       * ya tener la interfaz reclamada.
       */

      console.warn(
        "[ImpresoraManager] No fue posible reclamar la interfaz USB:",
        error,
      );
    }

    /*
     * Seleccionar alternate setting.
     */

    if (interfaz.alternateSetting !== undefined) {
      try {
        await device.selectAlternateInterface(
          interfaz.interfaceNumber,
          interfaz.alternateSetting,
        );
      } catch (error) {
        console.warn(
          "[ImpresoraManager] No fue posible seleccionar alternate setting:",
          error,
        );
      }
    }

    /*
     * Guardar endpoint OUT.
     */

    const endpoint = this.buscarEndpointSalidaUSB(interfaz);

    if (!endpoint) {
      throw new Error("No se encontró un endpoint USB de salida.");
    }

    this.usbEndpoint = endpoint;

    const impresora = this.registrarDispositivoUSB(device, true);

    impresora.interfaceNumber = interfaz.interfaceNumber;

    impresora.alternateSetting = interfaz.alternateSetting;

    impresora.endpointNumber = endpoint.endpointNumber;

    impresora.ultimaConexion = new Date().toISOString();

    this.impresoraActual = impresora;

    this.tipoConexionActual = TIPOS_CONEXION.USB;

    this.configuracion.tipoConexion = TIPOS_CONEXION.USB;

    this.guardarConfiguracion();

    this.impresoraConectada = true;

    this.cambiarEstado("conectada");

    this.emitir("conexion", impresora);

    this.emitir("impresora", impresora);

    return impresora;
  }

  /* ==============================================================
   * BUSCAR USB COMPATIBLE
   * ============================================================== */

  buscarUSBCompatible(dispositivos) {
    if (!this.impresoraActual) {
      return dispositivos[0] || null;
    }

    const vendorId = this.impresoraActual.vendorId;

    const productId = this.impresoraActual.productId;

    return (
      dispositivos.find(
        (device) =>
          device.vendorId === vendorId && device.productId === productId,
      ) ||
      dispositivos[0] ||
      null
    );
  }

  /* ==============================================================
   * BUSCAR INTERFAZ USB
   * ============================================================== */

  buscarInterfazUSB(device) {
    if (!device.configuration || !device.configuration.interfaces) {
      return null;
    }

    /*
     * Primero intentamos encontrar una
     * interfaz que tenga endpoint OUT.
     */

    for (const interfaz of device.configuration.interfaces) {
      for (const alternate of interfaz.alternates || []) {
        const tieneSalida = (alternate.endpoints || []).some(
          (endpoint) => endpoint.direction === "out",
        );

        if (tieneSalida) {
          return {
            interfaceNumber: interfaz.interfaceNumber,

            alternateSetting: alternate.alternateSetting,

            endpoints: alternate.endpoints || [],
          };
        }
      }
    }

    return null;
  }

  /* ==============================================================
   * BUSCAR ENDPOINT USB
   * ============================================================== */

  buscarEndpointSalidaUSB(interfaz) {
    if (!interfaz || !interfaz.endpoints) {
      return null;
    }

    return (
      interfaz.endpoints.find((endpoint) => endpoint.direction === "out") ||
      null
    );
  }

  /* ==============================================================
   * CONECTAR SERIAL
   * ============================================================== */

  async conectarSerial(opciones = {}) {
    if (!("serial" in navigator)) {
      throw new Error("Web Serial API no está disponible en este navegador.");
    }

    const forzarNuevo = opciones.forzarNuevo === true;

    try {
      let port = opciones.port || null;

      /*
       * ============================================================
       * MODO NORMAL
       * ============================================================
       * Si NO se solicita una nueva impresora:
       * 1. Usa el puerto actualmente conectado.
       * 2. Busca entre los puertos autorizados previamente.
       * 3. Si no encuentra ninguno, solicita uno nuevo.
       */
      if (!forzarNuevo) {
        if (!port) {
          port = this.serialPort;
        }

        if (!port) {
          const puertos = await navigator.serial.getPorts();

          if (puertos.length > 0) {
            port = this.buscarSerialCompatible(puertos);
          }
        }
      }

      /*
       * ============================================================
       * MODO "AGREGAR IMPRESORA"
       * ============================================================
       * Cuando forzarNuevo === true NO utilizamos:
       *
       *   this.serialPort
       *   navigator.serial.getPorts()
       *
       * porque queremos que el navegador muestre nuevamente
       * el selector para elegir OTRA impresora.
       */
      if (!port) {
        port = await navigator.serial.requestPort();
      }

      if (!port) {
        throw new Error("No se seleccionó ninguna impresora Serial.");
      }

      this.serialPort = port;

      /*
       * Abrir el puerto solamente si todavía no está abierto.
       */
      if (!port.readable && !port.writable) {
        await port.open({
          baudRate: opciones.baudRate || this.configuracion.baudRate || 9600,
          dataBits: opciones.dataBits || this.configuracion.dataBits || 8,
          stopBits: opciones.stopBits || this.configuracion.stopBits || 1,
          parity: opciones.parity || this.configuracion.parity || "none",
          bufferSize:
            opciones.bufferSize || this.configuracion.bufferSize || 255,
          flowControl:
            opciones.flowControl || this.configuracion.flowControl || "none",
        });
      }

      /*
       * Registrar la impresora.
       *
       * registrarPuertoSerial() debe conservar la impresora
       * anterior y agregar esta como una nueva si corresponde.
       */
      const impresora = this.registrarPuertoSerial(port, true);

      if (opciones.anchoPapel) {
        impresora.anchoPapel = opciones.anchoPapel;
        impresora.width = opciones.anchoPapel === 58 ? 32 : 48;
      }

      this.impresoraActual = impresora;
      this.impresoraConectada = true;

      this.emitir("conexion", {
        impresora,
        tipoConexion: "SERIAL",
      });

      this.emitir("impresora", impresora);

      return impresora;
    } catch (error) {
      const errorNormalizado = this.normalizarError(error);

      this.emitir("error", errorNormalizado);

      throw errorNormalizado;
    }
  }

  /* ==============================================================
   * BUSCAR SERIAL COMPATIBLE
   * ============================================================== */

  buscarSerialCompatible(puertos) {
    if (!this.impresoraActual) {
      return puertos[0] || null;
    }

    const vendorId = this.impresoraActual.usbVendorId;

    const productId = this.impresoraActual.usbProductId;

    return (
      puertos.find((port) => {
        const info = port.getInfo();

        return (
          String(info.usbVendorId || "") === String(vendorId || "") &&
          String(info.usbProductId || "") === String(productId || "")
        );
      }) ||
      puertos[0] ||
      null
    );
  }

  /* ==============================================================
   * CONECTAR BLUETOOTH
   * ============================================================== */

  async conectarBluetooth(opciones = {}) {
    if (!navigator.bluetooth) {
      throw new Error("Web Bluetooth no está disponible en este navegador.");
    }

    const forzarNuevo = opciones.forzarNuevo === true;

    /*
     * ============================================================
     * CONFIGURACIÓN
     * ============================================================
     */

    const bluetoothConfig = {
      ...this.configuracion.bluetooth,
      ...(this.impresoraActual?.bluetooth || {}),
      ...(opciones.bluetooth || {}),
    };

    /*
     * ============================================================
     * UUIDS QUE AUTORIZAREMOS AL NAVEGADOR
     * ============================================================
     */

    const optionalServices = [
      ...new Set([bluetoothConfig.serviceUUID, ...BLUETOOTH_SERVICE_UUIDS]),
    ].filter(Boolean);

    /*
     * ============================================================
     * SELECCIONAR DISPOSITIVO
     * ============================================================
     */

    let device = opciones.device || null;

    /*
     * Si NO estamos agregando una nueva impresora,
     * intentar reutilizar la anterior.
     */

    if (!forzarNuevo) {
      if (!device) {
        device = this.bluetoothDevice;
      }

      /*
       * Intentar recuperar dispositivos autorizados.
       */

      if (!device && typeof navigator.bluetooth.getDevices === "function") {
        try {
          const dispositivos = await navigator.bluetooth.getDevices();

          if (this.impresoraActual && this.impresoraActual.bluetoothId) {
            device = dispositivos.find(
              (item) => item.id === this.impresoraActual.bluetoothId,
            );
          }

          if (!device) {
            device = dispositivos[0];
          }
        } catch (error) {
          console.warn(
            "[ImpresoraManager] No se pudieron recuperar dispositivos Bluetooth:",
            error,
          );
        }
      }
    }

    /*
     * ============================================================
     * SOLICITAR NUEVO DISPOSITIVO
     * ============================================================
     */

    if (!device) {
      device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices,
      });
    }

    if (!device) {
      throw new Error("No se seleccionó ninguna impresora Bluetooth.");
    }

    console.log("[ImpresoraManager] Dispositivo Bluetooth seleccionado:", {
      id: device.id,
      name: device.name,
    });

    this.bluetoothDevice = device;

    /*
     * ============================================================
     * EVENTO DE DESCONEXIÓN
     * ============================================================
     */

    device.removeEventListener(
      "gattserverdisconnected",
      this._onBluetoothGattServerDisconnected,
    );

    device.addEventListener(
      "gattserverdisconnected",
      this._onBluetoothGattServerDisconnected,
    );

    /*
     * ============================================================
     * VALIDAR GATT
     * ============================================================
     */

    if (!device.gatt) {
      throw new Error(
        "La impresora seleccionada no expone GATT. " +
          "Es posible que utilice Bluetooth clásico/SPP.",
      );
    }

    /*
     * ============================================================
     * CONECTAR GATT
     * ============================================================
     */

    let server;

    if (device.gatt.connected) {
      server = device.gatt;
    } else {
      server = await device.gatt.connect();
    }

    this.bluetoothServer = server;

    console.log("[ImpresoraManager] GATT conectado:", device.name);

    /*
     * ============================================================
     * BUSCAR SERVICIO
     * ============================================================
     */

    const service = await this.buscarServicioBluetooth(
      server,
      bluetoothConfig.serviceUUID,
    );

    /*
     * ============================================================
     * BUSCAR CARACTERÍSTICA DE ESCRITURA
     * ============================================================
     */

    const characteristic = await this.buscarCaracteristicaBluetooth(
      service,
      bluetoothConfig.characteristicUUID,
    );

    /*
     * ============================================================
     * GUARDAR REFERENCIAS
     * ============================================================
     */

    this.bluetoothCharacteristic = characteristic;

    /*
     * ============================================================
     * GUARDAR UUID REAL EN CONFIGURACIÓN
     * ============================================================
     */

    bluetoothConfig.serviceUUID = service.uuid;

    bluetoothConfig.characteristicUUID = characteristic.uuid;

    console.log(
      "[ImpresoraManager] Configuración BLE detectada:",
      bluetoothConfig,
    );

    /*
     * ============================================================
     * REGISTRAR IMPRESORA
     * ============================================================
     */

    const impresora = this.registrarDispositivoBluetooth(device, true);

    impresora.bluetooth = {
      serviceUUID: service.uuid,
      characteristicUUID: characteristic.uuid,
    };

    impresora.bluetoothId = device.id;

    impresora.nombre = device.name || impresora.nombre || "Impresora Bluetooth";

    impresora.ultimaConexion = new Date().toISOString();

    /*
     * ============================================================
     * PAPEL
     * ============================================================
     */

    if (opciones.anchoPapel) {
      impresora.anchoPapel = opciones.anchoPapel;

      impresora.width = opciones.anchoPapel === 58 ? 32 : 48;
    }

    /*
     * ============================================================
     * ESTADO GLOBAL
     * ============================================================
     */

    this.impresoraActual = impresora;

    this.tipoConexionActual = TIPOS_CONEXION.BLUETOOTH;

    this.configuracion.tipoConexion = TIPOS_CONEXION.BLUETOOTH;

    this.configuracion.bluetooth = {
      ...bluetoothConfig,
      serviceUUID: service.uuid,
      characteristicUUID: characteristic.uuid,
    };

    this.impresoraConectada = true;

    this.cambiarEstado("conectada");

    this.guardarConfiguracion();

    this.emitir("conexion", {
      impresora,
      tipoConexion: TIPOS_CONEXION.BLUETOOTH,
    });

    this.emitir("impresora", impresora);

    console.log("[ImpresoraManager] Bluetooth conectado correctamente.", {
      impresora: impresora.nombre,
      serviceUUID: service.uuid,
      characteristicUUID: characteristic.uuid,
      write: characteristic.properties?.write,
      writeWithoutResponse: characteristic.properties?.writeWithoutResponse,
    });

    return impresora;
  }

  /* ==============================================================
   * DESCONECTAR
   * ============================================================== */

  async desconectar() {
    this.detenerReconectar = true;

    this.detenerMonitoreo();

    /*
     * USB
     */

    if (this.usbDevice) {
      try {
        if (this.usbDevice.opened) {
          await this.usbDevice.close();
        }
      } catch (error) {
        console.warn("[ImpresoraManager] Error cerrando USB:", error);
      }
    }

    /*
     * Serial
     */

    if (this.serialPort) {
      try {
        if (this.serialPort.readable || this.serialPort.writable) {
          await this.serialPort.close();
        }
      } catch (error) {
        console.warn("[ImpresoraManager] Error cerrando Serial:", error);
      }
    }

    /*
     * Bluetooth
     */

    if (this.bluetoothDevice && this.bluetoothDevice.gatt) {
      try {
        if (this.bluetoothDevice.gatt.connected) {
          this.bluetoothDevice.gatt.disconnect();
        }
      } catch (error) {
        console.warn("[ImpresoraManager] Error cerrando Bluetooth:", error);
      }
    }

    this.usbDevice = null;

    this.serialPort = null;

    this.bluetoothServer = null;

    this.bluetoothCharacteristic = null;

    this.impresoraConectada = false;

    this.cambiarEstado("desconectada");

    this.emitir("desconexion", this.impresoraActual);
  }

  /* ==============================================================
   * RECONEXIÓN
   * ============================================================== */

  async reconectar() {
    if (this.reconectando) {
      return false;
    }

    if (!this.impresoraActual) {
      return false;
    }

    this.reconectando = true;

    this.cambiarEstado("reconectando");

    try {
      const tipo = this.impresoraActual.tipoConexion;

      switch (tipo) {
        case TIPOS_CONEXION.USB:
          await this.conectarUSB({
            device: await this.obtenerUSBActual(),
          });
          break;

        case TIPOS_CONEXION.SERIAL:
          await this.conectarSerial({
            port: await this.obtenerSerialActual(),
          });
          break;

        case TIPOS_CONEXION.BLUETOOTH:
          await this.conectarBluetooth({
            device: await this.obtenerBluetoothActual(),
          });
          break;

        default:
          throw new Error("Tipo de conexión desconocido.");
      }

      this.reconectando = false;

      return true;
    } catch (error) {
      this.reconectando = false;

      this.impresoraConectada = false;

      this.cambiarEstado("desconectada");

      return false;
    }
  }

  /* ==============================================================
   * OBTENER USB ACTUAL
   * ============================================================== */

  async obtenerUSBActual() {
    if (this.usbDevice) {
      return this.usbDevice;
    }

    if (!navigator.usb) {
      return null;
    }

    const devices = await navigator.usb.getDevices();

    return (
      devices.find(
        (device) =>
          device.vendorId === this.impresoraActual.vendorId &&
          device.productId === this.impresoraActual.productId,
      ) || null
    );
  }

  /* ==============================================================
   * OBTENER SERIAL ACTUAL
   * ============================================================== */

  async obtenerSerialActual() {
    if (this.serialPort) {
      return this.serialPort;
    }

    if (!navigator.serial) {
      return null;
    }

    const ports = await navigator.serial.getPorts();

    return this.buscarSerialCompatible(ports);
  }

  /* ==============================================================
   * OBTENER BLUETOOTH ACTUAL
   * ============================================================== */

  async obtenerBluetoothActual() {
    if (this.bluetoothDevice) {
      return this.bluetoothDevice;
    }

    if (
      !navigator.bluetooth ||
      typeof navigator.bluetooth.getDevices !== "function"
    ) {
      return null;
    }

    const devices = await navigator.bluetooth.getDevices();

    return (
      devices.find(
        (device) => device.id === this.impresoraActual.bluetoothId,
      ) || null
    );
  }

  /* ==============================================================
   * IMPRIMIR
   * ============================================================== */

  resizeImage = (url, maxWidth = 800, maxHeight = 800, quality = 0.9) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      // Prevent CORS errors if loading from an external domain
      img.crossOrigin = "anonymous";
      img.src = url;

      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        // Create an in-memory canvas
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = maxHeight;

        const ctx = canvas.getContext("2d");

        // Optional: Set smoothing quality for better visual results
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";

        // Draw the smaller image onto the canvas
        ctx.drawImage(img, 0, 0, width, height);

        // Export as a lightweight compressed Blob (prefer toBlob over toDataURL for performance)
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error("Canvas compression to Blob failed."));
            }
          },
          "image/png", // Use 'image/jpeg' or 'image/webp' for lossy quality compression
          quality,
        );
      };

      img.onerror = (err) =>
        reject(new Error("Failed to load image from URL."));
    });
  };

  mergeImagesForPrinter = (imgUrl1, imgUrl2, printerWidth = 384) => {
    return new Promise((resolve, reject) => {
      const img1 = new Image();
      const img2 = new Image();

      let loadedCount = 0;
      const onImageLoad = () => {
        loadedCount++;
        if (loadedCount === 2) {
          // Create canvas matching the target printer width
          const canvas = document.createElement("canvas");
          canvas.width = printerWidth;

          // Calculate dynamic heights to preserve aspect ratios at 50% width each
          const targetWidthPerImg = printerWidth / 2;
          const h1 = (targetWidthPerImg / img1.width) * img1.height;
          const h2 = (targetWidthPerImg / img2.width) * img2.height;
          const y = (canvas.height - img2.height) / 2;

          // Canvas height must fit the taller image
          canvas.height = Math.max(h1, h2);
          const ctx = canvas.getContext("2d");

          // Fill canvas background with white (Thermal printers don't print transparency)
          ctx.fillStyle = "#FFFFFF";
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          // Draw the two images side-by-side
          ctx.drawImage(img1, 0, 0, targetWidthPerImg, h1);
          ctx.drawImage(
            img2,
            targetWidthPerImg,
            -y,
            targetWidthPerImg,
            canvas.height,
          );

          // Export as a single image payload
          resolve(canvas.toDataURL("image/png"));
        }
      };

      img1.onload = onImageLoad;
      img2.onload = onImageLoad;
      img1.onerror = reject;
      img2.onerror = reject;

      img1.src = imgUrl1;
      img2.src = imgUrl2;
    });
  };

  async print(orden, registro, empleado, cocina) {
    const imglogo = URL.createObjectURL(
      await this.resizeImage(
        registro.imgticket ? registro.imgticket : registro.img,
        80,
        80,
      ),
    );
    const imglogor = URL.createObjectURL(
      await this.resizeImage(
        "/static/media/logonaranja.47703c147962eb0583b9.png",
        180,
        180,
      ),
    );
    // const combinedBase64 = await mergeImagesForPrinter(imglogo, imglogor, 360);
    const combinedBase64 = await this.mergeImagesForPrinter(
      imglogo,
      imglogor,
      320,
    );
    const encoder = (text) => new TextEncoder().encode(text.replace("ñ", "n"));
    const width = this.configuracion.width;
    const receipt = (
      <Printer type="epson" width={width} encoder={encoder}>
        {orden?.fecha ? (
          <>
            {!cocina && (
              <>
                <Imag
                  src={combinedBase64}
                  align="center"
                  transforms={[transforms.floydSteinberg]}
                />
                <Text bold={true} size={{ width: 2, height: 2 }} align="center">
                  {registro.nameticket}
                </Text>
                <Text align="center">Razon social:</Text>
                <Text align="center">
                  {registro.razon_socialticket ? registro.razon_social : ""}
                </Text>
                <Text align="center">
                  {registro.calleticket
                    ? registro.calleticket +
                      " #" +
                      registro.numdomticket +
                      " Col." +
                      registro.coloniaticket +
                      " C.P. " +
                      registro.codigopticket
                    : ""}
                </Text>
                <Text align="center">
                  {registro.municipioticket
                    ? registro.municipioticket +
                      ", " +
                      registro.estadoticket +
                      " " +
                      registro.paisticket
                    : ""}
                </Text>
                <Text align="center">
                  {registro.sucursalticket
                    ? "Sucursal:" + registro.sucursalticket
                    : ""}
                </Text>
                <Text align="center">
                  {registro.rfcticket ? "RFC:" + registro.rfcticket : ""}
                </Text>
                <Text align="center">
                  {registro.phoneticket
                    ? "Telefono: " + registro.phoneticket
                    : ""}
                </Text>
                <Br />
              </>
            )}
            {width < 48 ? (
              <>
                <Text align="left">Folio: {orden.folio}</Text>
                <Text align="left">
                  Fecha y hora:{" "}
                  {orden?.fecha.seconds
                    ? new Date(
                        orden?.fecha.seconds * 1000,
                      ).toLocaleDateString() +
                      " " +
                      new Date(orden?.fecha.seconds * 1000).toLocaleTimeString()
                    : orden?.fecha.toLocaleDateString() +
                      " " +
                      orden?.fecha.toLocaleTimeString()}
                </Text>
              </>
            ) : (
              <Row
                left={<Text align="left">Folio: {orden.folio}</Text>}
                right={
                  <Text align="left">
                    Fecha y hora:{" "}
                    {orden?.fecha.seconds
                      ? new Date(
                          orden?.fecha.seconds * 1000,
                        ).toLocaleDateString() +
                        " " +
                        new Date(
                          orden?.fecha.seconds * 1000,
                        ).toLocaleTimeString()
                      : orden?.fecha.toLocaleDateString() +
                        " " +
                        orden?.fecha.toLocaleTimeString()}
                  </Text>
                }
              />
            )}
            {orden.table ? <Text align="left">Mesa: {orden.table}</Text> : ""}
            <Text align="left">Cliente: {orden.user_name}</Text>
            <Text align="left">Atendido por: {empleado}</Text>
            <Br />
            {!cocina ? (
              <Row left={"Producto(precio):cantidad"} right={"Total"} />
            ) : (
              <Row left={"Producto"} right={"Cantidad"} />
            )}

            <Br />
            {orden.detalle.map((item, index) => (
              <>
                {!cocina ? (
                  <Row
                    left={
                      item.name +
                      "(" +
                      "$" +
                      parseFloat(item.costo).toFixed(2) +
                      "):" +
                      item.cantidad
                    }
                    right={<Text>${parseFloat(item.total).toFixed(2)} </Text>}
                  />
                ) : (
                  <Row
                    left={<Text>{item.name}</Text>}
                    right={<Text>{item.cantidad}</Text>}
                  />
                )}

                {item.ingredientes &&
                Object.keys(item.ingredientes).length > 0 ? (
                  <Text>-Ingredientes</Text>
                ) : (
                  ""
                )}
                {item.ingredientes ? (
                  Object.keys(item.ingredientes).map((obj, i) => {
                    return (
                      <Text>
                        --{item.ingredientes[obj].name}
                        {": " + item.ingredientes[obj].cantidad}
                      </Text>
                    );
                  })
                ) : (
                  <></>
                )}
                {item.anadidos && Object.keys(item.anadidos).length > 0 ? (
                  <Text>-Añadidos</Text>
                ) : (
                  ""
                )}
                {item.anadidos ? (
                  Object.keys(item.anadidos).map((obj, i) => {
                    return (
                      <Text>
                        --{item.anadidos[obj].name}(
                        {!cocina &&
                        parseFloat(
                          item.anadidos[obj].precio *
                            item.anadidos[obj].cantidad,
                        ) > 0
                          ? "+$" +
                            (
                              item.anadidos[obj].precio *
                              item.anadidos[obj].cantidad
                            ).toFixed(2)
                          : parseFloat(item.anadidos[obj].costo) > 0
                            ? "$" +
                              (
                                parseFloat(item.anadidos[obj].costo) *
                                item.anadidos[obj].cantidad
                              ).toFixed(2)
                            : "Gratis"}
                        {cocina && item.anadidos[obj].cantidad})
                      </Text>
                    );
                  })
                ) : (
                  <></>
                )}
              </>
            ))}
            {!cocina && (
              <>
                <Line />
                <Text align="right">
                  Subtotal: ${(orden.total / (1 + orden.iva / 100)).toFixed(2)}
                </Text>
                <Text align="right">
                  IVA ({orden.iva}%): ${orden.cobroiva.toFixed(2)}
                </Text>
                <Text align="right">IEPS: $0.00</Text>
                <Text align="right">Total: ${orden.total.toFixed(2)}</Text>
                <Text align="right">
                  {window.convertir.NumerosALetras(orden.total.toFixed(2))}
                </Text>
                {width < 40 && <Br />}
                {/* <Text align="left">Total: ${orden.total.toFixed(2)}</Text> */}
                <Text align="left">
                  Puntos utilizados: {orden.puntosusados}
                </Text>
                <Line />
                <Text align="left">
                  Nuevo total: $
                  {(
                    orden.total -
                    orden.puntosusados *
                      (orden.equivalenciapuntos
                        ? parseFloat(orden.equivalenciapuntos)
                        : 0)
                  ).toFixed(2)}
                </Text>
                <Text align="left">
                  Pago: ${parseFloat(orden.cobro).toFixed(2)}
                </Text>
                <Text style={{ flex: 1, flexGrow: 1, fontSize: 5.5 }}>
                  Cambio: $
                  {orden.cambio < 0
                    ? (-parseFloat(orden.cambio)).toFixed(2)
                    : parseFloat(orden.cambio).toFixed(2)}
                </Text>
                <Text align="left">Metodo de pago: {orden.tipo_pago}</Text>
                <Text align="left">Pago en una sola exhibicion.</Text>
                {registro.Tpuntos ? (
                  <Text align="left">
                    Puntos generados: {orden.puntosgenerados}
                  </Text>
                ) : (
                  <></>
                )}
                <Br />
                <Text align="center">FACTURACION</Text>
                <Text align="center">
                  Estimado cliente si usted desea facturar su compra puede
                  hacerlo mediante el siguiente codigo QR, ahi podra realizar la
                  factura por su consumo.
                </Text>
                <QRCode
                  align="center"
                  cellSize={8}
                  content={"https://quickchart.io/qr?text=QRT-" + orden.id}
                />
                <Text align="center">Codigo de su orden:</Text>
                <Text align="center">{" " + orden.id}</Text>
                <Text align="center">¡Gracias por su compra!</Text>
                <Text align="center">
                  Queremos su opinion, califica nuestro servicio por medio del
                  QR.
                </Text>
              </>
            )}
          </>
        ) : (
          <></>
        )}
        <Cut lineFeeds={4} />
        <Cashdraw pin="5pin" />
      </Printer>
    );
    return receipt;
  }

  async imprimir(orden, registro, empleado, cocina, opciones = {}) {
    const receipt = await this.print(orden, registro, empleado, cocina);

    const idTrabajo = `print_${Date.now()}_${++this.idTrabajoImpresion}`;

    const tarea = {
      id: idTrabajo,

      receipt,

      opciones: {
        ...opciones,
        receipt,
      },

      creada: Date.now(),

      estado: "pendiente",

      ordenId: orden?.id ?? orden?.folio ?? null,

      folio: orden?.folio ?? orden?.id ?? "Sin folio",

      nombre: opciones.nombre || (cocina ? "Comanda cocina" : "Ticket"),

      cocina: Boolean(cocina),

      impresora: this.impresoraActual
        ? {
            id: this.impresoraActual.id,
            nombre: this.impresoraActual.nombre,
            tipoConexion: this.impresoraActual.tipoConexion,
          }
        : null,

      intentos: 0,

      fechaInicio: null,

      fechaFin: null,

      error: null,
    };

    this.colaImpresion.push(tarea);

    this.emitirCambioCola();

    /*
     * Procesar inmediatamente.
     */
    const resultado = this.procesarCola();

    return {
      ...resultado,
      idTrabajo,
      tarea,
    };
  }

  /* ==============================================================
   * PROCESAR COLA
   * ============================================================== */

  async procesarCola() {
    if (this.imprimiendo) {
      return {
        exito: false,
        enCola: true,
        mensaje: "La impresión fue agregada a la cola.",
        pendientes: this.obtenerColaPendiente(),
      };
    }

    this.imprimiendo = true;

    try {
      while (this.colaImpresion.length > 0) {
        const tarea = this.colaImpresion[0];

        this.trabajoImprimiendo = tarea;

        tarea.estado = "imprimiendo";
        tarea.fechaInicio = Date.now();
        tarea.intentos++;

        this.emitirCambioCola();

        try {
          await this.imprimirInterno(tarea.receipt, tarea.opciones);

          /*
           * Impresión exitosa.
           */
          tarea.estado = "impreso";
          tarea.fechaFin = Date.now();
          tarea.error = null;

          /*
           * Guardar en historial.
           */
          this.historialImpresion.unshift({
            ...tarea,
          });

          /*
           * Limitar historial.
           */
          if (this.historialImpresion.length > this.maxHistorialImpresion) {
            this.historialImpresion = this.historialImpresion.slice(
              0,
              this.maxHistorialImpresion,
            );
          }

          /*
           * Eliminar de pendientes.
           */
          this.colaImpresion.shift();

          this.trabajoImprimiendo = null;

          this.emitirCambioCola();
        } catch (error) {
          tarea.estado = "error";

          tarea.error = {
            mensaje: error?.message || String(error),
            nombre: error?.name || "Error",
          };

          this.emitirCambioCola();

          /*
           * Intentar recuperar la impresión.
           */
          const recuperado = await this.manejarFalloImpresion(tarea, error);

          if (recuperado) {
            tarea.estado = "impreso";
            tarea.fechaFin = Date.now();
            tarea.error = null;

            this.historialImpresion.unshift({
              ...tarea,
            });

            if (this.historialImpresion.length > this.maxHistorialImpresion) {
              this.historialImpresion = this.historialImpresion.slice(
                0,
                this.maxHistorialImpresion,
              );
            }

            this.colaImpresion.shift();

            this.trabajoImprimiendo = null;

            this.emitirCambioCola();
          } else {
            /*
             * IMPORTANTE:
             * NO eliminamos la tarea.
             *
             * Se queda visible en la cola
             * para que el usuario pueda
             * reintentarla manualmente.
             */

            this.trabajoImprimiendo = null;

            this.emitirCambioCola();

            break;
          }
        }
      }

      return {
        exito: this.colaImpresion.length === 0,
        pendientes: this.obtenerColaPendiente(),
      };
    } finally {
      this.imprimiendo = false;

      this.emitirCambioCola();
    }
  }

  obtenerCola() {
    return this.colaImpresion.map((tarea) => ({
      ...tarea,
      receipt: undefined,
    }));
  }

  obtenerColaPendiente() {
    return this.colaImpresion
      .filter(
        (tarea) =>
          tarea.estado === "pendiente" ||
          tarea.estado === "imprimiendo" ||
          tarea.estado === "error",
      )
      .map((tarea) => ({
        ...tarea,
        receipt: undefined,
      }));
  }

  obtenerHistorialImpresion() {
    return this.historialImpresion.map((tarea) => ({
      ...tarea,
      receipt: undefined,
    }));
  }

  /* ==============================================================
   * IMPRESIÓN INTERNA
   * ============================================================== */

  async imprimirInterno(receipt, opciones = {}) {
    /*
     * Asegurar conexión.
     */

    if (!this.impresoraConectada) {
      const conectado = await this.reconectar();

      if (!conectado) {
        throw new Error("La impresora no está conectada.");
      }
    }

    /*
     * Convertir React Printer a ESC/POS.
     */

    let data;

    if (receipt instanceof Uint8Array) {
      data = receipt;
    } else if (receipt instanceof ArrayBuffer) {
      data = new Uint8Array(receipt);
    } else {
      data = await render(receipt);
    }

    data = convertirUint8Array(data);

    this.cambiarEstado("imprimiendo");

    this.emitir("impresion", {
      estado: "iniciando",

      impresora: this.impresoraActual,
    });

    try {
      await this.escribir(data, opciones);

      this.emitir("impresion", {
        estado: "completada",

        impresora: this.impresoraActual,
      });

      this.cambiarEstado("conectada");

      return {
        exito: true,

        impresora: this.impresoraActual,

        bytes: data.byteLength,
      };
    } catch (error) {
      this.impresoraConectada = false;

      this.cambiarEstado("desconectada");

      this.emitir("desconexion", this.impresoraActual);

      throw error;
    }
  }

  /* ==============================================================
   * ESCRIBIR DATOS
   * ============================================================== */

  async escribir(data, opciones = {}) {
    const timeout = opciones.timeout || this.configuracion.timeout;

    const tipo = this.tipoConexionActual || this.configuracion.tipoConexion;

    const promesa = this.escribirSegunConexion(data, tipo);

    return this.conTimeout(
      promesa,
      timeout,
      "Tiempo agotado enviando datos a la impresora.",
    );
  }

  /* ==============================================================
   * ESCRIBIR SEGÚN CONEXIÓN
   * ============================================================== */

  async escribirSegunConexion(data, tipo) {
    switch (tipo) {
      case TIPOS_CONEXION.USB:
        return this.escribirUSB(data);

      case TIPOS_CONEXION.SERIAL:
        return this.escribirSerial(data);

      case TIPOS_CONEXION.BLUETOOTH:
        return this.escribirBluetooth(data);

      default:
        throw new Error("No existe una conexión de impresora activa.");
    }
  }

  /* ==============================================================
   * ESCRIBIR USB
   * ============================================================== */

  async escribirUSB(data) {
    if (!this.usbDevice) {
      throw new Error("No existe dispositivo USB conectado.");
    }

    if (!this.usbDevice.opened) {
      await this.usbDevice.open();
    }

    if (!this.usbEndpoint) {
      const interfaz = this.buscarInterfazUSB(this.usbDevice);

      if (!interfaz) {
        throw new Error("No se encontró interfaz USB.");
      }

      this.usbEndpoint = this.buscarEndpointSalidaUSB(interfaz);
    }

    if (!this.usbEndpoint) {
      throw new Error("No se encontró endpoint USB de salida.");
    }

    /*
     * WebUSB permite enviar los bytes
     * mediante transferOut().
     */

    const resultado = await this.usbDevice.transferOut(
      this.usbEndpoint.endpointNumber,
      data,
    );

    return resultado;
  }

  /* ==============================================================
   * ESCRIBIR SERIAL
   * ============================================================== */

  async escribirSerial(data) {
    if (!this.serialPort) {
      throw new Error("No existe puerto Serial conectado.");
    }

    if (!this.serialPort.writable) {
      throw new Error("El puerto Serial no está disponible para escritura.");
    }

    const writer = this.serialPort.writable.getWriter();

    try {
      await writer.write(data);
    } finally {
      writer.releaseLock();
    }

    return true;
  }

  /* ==============================================================
   * ESCRIBIR BLUETOOTH
   * ============================================================== */

  /* ==============================================================
   * ESCRIBIR BLUETOOTH
   * ==============================================================
   */
  async escribirBluetooth(data) {
    /*
     * ============================================================
     * ASEGURAR CONEXIÓN ANTES DE COMENZAR
     * ============================================================
     */
    await this.asegurarBluetoothConectado();

    if (!this.bluetoothCharacteristic) {
      throw new Error("No existe característica Bluetooth de impresión.");
    }

    /*
     * Bluetooth BLE suele tener un límite pequeño
     * por paquete.
     *
     * 180 bytes es un valor conservador.
     */
    const chunkSize = 180;

    for (let offset = 0; offset < data.length; offset += chunkSize) {
      const chunk = data.slice(
        offset,
        Math.min(offset + chunkSize, data.length),
      );

      /*
       * ==========================================================
       * PRIMER INTENTO
       * ==========================================================
       */
      try {
        /*
         * Verificar que GATT siga conectado
         * justo antes de escribir.
         */
        if (!this.bluetoothDevice?.gatt?.connected) {
          await this.asegurarBluetoothConectado();
        }

        const characteristic = this.bluetoothCharacteristic;

        if (!characteristic) {
          throw new Error("La característica Bluetooth no está disponible.");
        }

        /*
         * --------------------------------------------------------
         * Preferir writeWithoutResponse
         * --------------------------------------------------------
         */
        if (characteristic.properties?.writeWithoutResponse) {
          await characteristic.writeValueWithoutResponse(chunk);
        } else if (characteristic.properties?.write) {
          await characteristic.writeValueWithResponse(chunk);
        } else {
          /*
           * Compatibilidad con navegadores antiguos.
           */
          await characteristic.writeValue(chunk);
        }
      } catch (error) {
        /*
         * ========================================================
         * DETECTAR DESCONEXIÓN GATT
         * ========================================================
         */
        const mensaje = error?.message || String(error);

        const esDesconexion =
          error?.name === "NetworkError" ||
          mensaje.includes("GATT Server is disconnected") ||
          mensaje.includes("GATT server is disconnected") ||
          mensaje.includes(
            "GATT Server is disconnected. Cannot perform GATT operations",
          );

        /*
         * Si el error NO corresponde a una desconexión,
         * no ocultarlo.
         */
        if (!esDesconexion) {
          throw error;
        }

        console.warn(
          "[ImpresoraManager] GATT se desconectó durante la escritura.",
        );

        console.warn("[ImpresoraManager] Intentando reconectar Bluetooth...");

        /*
         * ========================================================
         * RECONEXIÓN
         * ========================================================
         */
        await this.asegurarBluetoothConectado();

        /*
         * ========================================================
         * SEGUNDO INTENTO
         * ========================================================
         *
         * MUY IMPORTANTE:
         *
         * utilizamos this.bluetoothCharacteristic nuevamente,
         * porque asegurarBluetoothConectado() obtuvo una
         * característica nueva.
         */
        const nuevaCaracteristica = this.bluetoothCharacteristic;

        if (!nuevaCaracteristica) {
          throw new Error(
            "No fue posible recuperar la característica Bluetooth.",
          );
        }

        if (nuevaCaracteristica.properties?.writeWithoutResponse) {
          await nuevaCaracteristica.writeValueWithoutResponse(chunk);
        } else if (nuevaCaracteristica.properties?.write) {
          await nuevaCaracteristica.writeValueWithResponse(chunk);
        } else {
          await nuevaCaracteristica.writeValue(chunk);
        }
      }

      /*
       * ==========================================================
       * PEQUEÑA PAUSA ENTRE PAQUETES
       * ==========================================================
       */
      if (offset + chunkSize < data.length) {
        await dormir(40);
      }
    }

    return true;
  }

  /* ==============================================================
   * MANEJAR FALLO DE IMPRESIÓN
   * ============================================================== */

  async manejarFalloImpresion(tarea, error) {
    console.error("[ImpresoraManager] Falló la impresión:", error);

    /*
     * Primero intentamos reconectar
     * la misma impresora.
     */

    for (let intento = 0; intento < this.configuracion.reintentos; intento++) {
      try {
        await dormir(500 * (intento + 1));

        const conectado = await this.reconectar();

        if (!conectado) {
          continue;
        }

        await this.imprimirInterno(tarea.receipt, tarea.opciones);

        return true;
      } catch (errorReconectar) {
        console.warn("[ImpresoraManager] Reintento fallido:", errorReconectar);
      }
    }

    /*
     * Si la misma impresora no responde,
     * pedir otra.
     */

    const otra = await this.solicitarOtraImpresora({
      error,

      impresora: this.impresoraActual,

      impresoras: this.listarImpresoras(),
    });

    if (!otra) {
      return false;
    }

    try {
      await this.seleccionar(otra);

      await this.imprimirInterno(tarea.receipt, tarea.opciones);

      return true;
    } catch (errorOtra) {
      console.error(
        "[ImpresoraManager] También falló la nueva impresora:",
        errorOtra,
      );

      return false;
    }
  }

  /* ==============================================================
   * SOLICITAR OTRA IMPRESORA
   * ============================================================== */

  async solicitarOtraImpresora(datos) {
    if (typeof this.onSeleccionarOtraImpresora === "function") {
      return this.onSeleccionarOtraImpresora(datos);
    }

    /*
     * Si React no ha registrado un callback,
     * devolvemos null.
     */

    return null;
  }

  /* ==============================================================
   * SELECCIONAR IMPRESORA
   * ============================================================== */

  async seleccionar(impresora) {
    if (!impresora) {
      throw new Error("No se especificó una impresora.");
    }

    await this.desconectar();

    this.impresoraActual = {
      ...impresora,
    };

    this.tipoConexionActual = impresora.tipoConexion;

    this.configuracion.tipoConexion = impresora.tipoConexion;

    if (impresora.anchoPapel) {
      this.configurarTamano(impresora.anchoPapel);
    }

    this.guardarConfiguracion();

    return this.conectar({
      impresora: this.impresoraActual,
    });
  }

  /* ==============================================================
   * ELIMINAR IMPRESORA
   * ============================================================== */

  eliminarImpresora(id) {
    this.impresorasGuardadas = this.impresorasGuardadas.filter(
      (impresora) => impresora.id !== id,
    );

    if (this.impresoraActual && this.impresoraActual.id === id) {
      this.impresoraActual = null;

      this.impresoraConectada = false;
    }

    this.guardarConfiguracion();

    this.emitir("impresora", this.impresoraActual);
  }

  /* ==============================================================
   * ESTADO
   * ============================================================== */

  obtenerEstado() {
    return {
      impresoraConectada: this.impresoraConectada,

      impresoraActual: this.impresoraActual,

      tipoConexion: this.tipoConexionActual,

      estado: this.estado,

      error: this.errorActual,

      anchoPapel: this.configuracion.anchoPapel,

      width: this.configuracion.width,

      impresoras: this.listarImpresoras(),

      cola: this.colaImpresion.length,

      colaDetalle: this.obtenerColaPendiente(),

      trabajoImprimiendo: this.trabajoImprimiendo
        ? {
            ...this.trabajoImprimiendo,
            receipt: undefined,
          }
        : null,

      historialImpresion: this.obtenerHistorialImpresion(),
    };
  }

  /* ==============================================================
   * CAMBIAR ESTADO
   * ============================================================== */

  cambiarEstado(estado) {
    this.estado = estado;

    this.emitir("estado", this.obtenerEstado());
  }

  /* ==============================================================
   * EVENTOS
   * ============================================================== */

  on(evento, callback) {
    if (!this.listeners[evento]) {
      throw new Error(`Evento desconocido: ${evento}`);
    }

    this.listeners[evento].add(callback);

    return () => {
      this.off(evento, callback);
    };
  }

  off(evento, callback) {
    if (!this.listeners[evento]) {
      return;
    }

    this.listeners[evento].delete(callback);
  }

  emitir(evento, datos) {
    if (!this.listeners[evento]) {
      return;
    }

    for (const callback of this.listeners[evento]) {
      try {
        callback(datos);
      } catch (error) {
        console.error(`[ImpresoraManager] Error en listener ${evento}:`, error);
      }
    }
  }

  /* ==============================================================
   * LISTENERS DEL NAVEGADOR
   * ============================================================== */

  instalarListenersNavegador() {
    if ("usb" in navigator && navigator.usb) {
      navigator.usb.removeEventListener("connect", this._onUsbConnect);

      navigator.usb.removeEventListener("disconnect", this._onUsbDisconnect);

      navigator.usb.addEventListener("connect", this._onUsbConnect);

      navigator.usb.addEventListener("disconnect", this._onUsbDisconnect);
    }

    if ("serial" in navigator && navigator.serial) {
      navigator.serial.removeEventListener("connect", this._onSerialConnect);

      navigator.serial.removeEventListener(
        "disconnect",
        this._onSerialDisconnect,
      );

      navigator.serial.addEventListener("connect", this._onSerialConnect);

      navigator.serial.addEventListener("disconnect", this._onSerialDisconnect);
    }
  }

  /* ==============================================================
   * EVENTO USB CONNECT
   * ============================================================== */

  async _onUsbConnect(event) {
    const device = event.device;

    this.registrarDispositivoUSB(device, true);

    /*
     * Si corresponde a la impresora actual,
     * intentar reconectar.
     */

    if (
      this.impresoraActual &&
      this.impresoraActual.tipoConexion === TIPOS_CONEXION.USB &&
      this.impresoraActual.vendorId === device.vendorId &&
      this.impresoraActual.productId === device.productId
    ) {
      await this.reconectar();
    }
  }

  /* ==============================================================
   * EVENTO USB DISCONNECT
   * ============================================================== */

  async _onUsbDisconnect(event) {
    const device = event.device;

    if (this.usbDevice === device) {
      this.usbDevice = null;

      this.usbEndpoint = null;

      this.impresoraConectada = false;

      this.cambiarEstado("desconectada");

      this.emitir("desconexion", this.impresoraActual);

      this.iniciarReconectarAutomaticamente();
    }
  }

  /* ==============================================================
   * EVENTO SERIAL CONNECT
   * ============================================================== */

  async _onSerialConnect(event) {
    const port = event.target || event.port;

    this.registrarPuertoSerial(port, true);

    if (
      this.impresoraActual &&
      this.impresoraActual.tipoConexion === TIPOS_CONEXION.SERIAL
    ) {
      await this.reconectar();
    }
  }

  /* ==============================================================
   * EVENTO SERIAL DISCONNECT
   * ============================================================== */

  async _onSerialDisconnect(event) {
    const port = event.target || event.port;

    if (this.serialPort === port) {
      this.serialPort = null;

      this.impresoraConectada = false;

      this.cambiarEstado("desconectada");

      this.emitir("desconexion", this.impresoraActual);

      this.iniciarReconectarAutomaticamente();
    }
  }

  /* ==============================================================
   * EVENTO BLUETOOTH DISCONNECT
   * ============================================================== */

  _onBluetoothGattServerDisconnected(event) {
    console.warn("[ImpresoraManager] Bluetooth GATT desconectado.");

    /*
     * Limpiar referencias GATT.
     *
     * NO eliminamos bluetoothDevice porque ese dispositivo
     * todavía puede volver a conectarse.
     */
    this.bluetoothServer = null;

    this.bluetoothCharacteristic = null;

    this.impresoraConectada = false;

    this.cambiarEstado("desconectada");

    this.emitir("desconexion", this.impresoraActual);

    /*
     * Intentar reconectar automáticamente.
     */
    this.iniciarReconectarAutomaticamente();
  }

  /* ==============================================================
   * RECONEXIÓN AUTOMÁTICA
   * ============================================================== */

  iniciarReconectarAutomaticamente() {
    if (this.intervaloReconectar) {
      return;
    }

    this.detenerReconectar = false;

    this.intervaloReconectar = setInterval(async () => {
      if (this.detenerReconectar) {
        return;
      }

      if (this.impresoraConectada) {
        this.detenerReconectarAutomaticamente();
        return;
      }

      const ahora = Date.now();

      /*
       * Evitar intentos demasiado frecuentes.
       */

      if (ahora - this.ultimoIntentoReconectar < 3000) {
        return;
      }

      this.ultimoIntentoReconectar = ahora;

      await this.reconectar();
    }, 3000);
  }

  /* ==============================================================
   * DETENER RECONEXIÓN AUTOMÁTICA
   * ============================================================== */

  detenerReconectarAutomaticamente() {
    if (this.intervaloReconectar) {
      clearInterval(this.intervaloReconectar);

      this.intervaloReconectar = null;
    }
  }

  /* ==============================================================
   * MONITOREO
   * ============================================================== */

  iniciarMonitoreo() {
    this.detenerMonitoreo();

    /*
     * No hacemos polling agresivo.
     *
     * Las APIs USB/Serial/Bluetooth
     * notifican desconexiones mediante
     * eventos cuando es posible.
     */

    this.intervaloMonitoreo = setInterval(async () => {
      if (!this.impresoraConectada) {
        return;
      }

      try {
        const disponible = await this.comprobarConexion();

        if (!disponible) {
          this.impresoraConectada = false;

          this.cambiarEstado("desconectada");

          this.emitir("desconexion", this.impresoraActual);

          this.iniciarReconectarAutomaticamente();
        }
      } catch (error) {
        console.warn("[ImpresoraManager] Error comprobando conexión:", error);
      }
    }, 5000);
  }

  /* ==============================================================
   * DETENER MONITOREO
   * ============================================================== */

  detenerMonitoreo() {
    if (this.intervaloMonitoreo) {
      clearInterval(this.intervaloMonitoreo);

      this.intervaloMonitoreo = null;
    }
  }

  /* ==============================================================
   * COMPROBAR CONEXIÓN
   * ============================================================== */

  async comprobarConexion() {
    const tipo = this.tipoConexionActual;

    switch (tipo) {
      case TIPOS_CONEXION.USB:
        return !!this.usbDevice && this.usbDevice.opened;

      case TIPOS_CONEXION.SERIAL:
        return !!this.serialPort && !!this.serialPort.writable;

      case TIPOS_CONEXION.BLUETOOTH:
        return (
          !!this.bluetoothDevice &&
          !!this.bluetoothDevice.gatt &&
          this.bluetoothDevice.gatt.connected &&
          !!this.bluetoothCharacteristic
        );

      default:
        return false;
    }
  }

  /* ==============================================================
   * CON TIMEOUT
   * ============================================================== */

  conTimeout(promise, timeout, mensaje) {
    return Promise.race([
      promise,

      new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error(mensaje));
        }, timeout);
      }),
    ]);
  }

  /* ==============================================================
   * NORMALIZAR ERROR
   * ============================================================== */

  normalizarError(error) {
    return {
      mensaje: error?.message || String(error),

      nombre: error?.name || "Error",

      original: error,
    };
  }

  /* ==============================================================
   * CONFIGURAR CALLBACK DE OTRA IMPRESORA
   * ============================================================== */

  establecerSelectorDeImpresora(callback) {
    this.onSeleccionarOtraImpresora = callback;
  }

  /* ==============================================================
   * OBTENER IMPRESORA ACTUAL
   * ============================================================== */

  obtenerImpresoraActual() {
    return this.impresoraActual;
  }

  /* ==============================================================
   * OBTENER ESTADO DE CONEXIÓN
   * ============================================================== */

  estaConectada() {
    return this.impresoraConectada;
  }

  /* ==============================================================
   * ASEGURAR CONEXIÓN BLUETOOTH
   * ==============================================================
   *
   * Verifica que el GATT continúe conectado.
   *
   * Si la impresora se desconectó:
   *
   *   1. Reconecta GATT.
   *   2. Obtiene nuevamente el servicio.
   *   3. Obtiene nuevamente la característica.
   *
   * Esto es importante porque una BluetoothRemoteGATTCharacteristic
   * perteneciente a una conexión anterior no debe reutilizarse
   * después de una desconexión.
   */
  async asegurarBluetoothConectado() {
    if (!this.bluetoothDevice) {
      throw new Error("No hay una impresora Bluetooth seleccionada.");
    }

    const device = this.bluetoothDevice;

    if (!device.gatt) {
      throw new Error("La impresora Bluetooth no expone GATT.");
    }

    /*
     * ============================================================
     * CONFIGURACIÓN
     * ============================================================
     */

    const bluetoothConfig = {
      ...this.configuracion.bluetooth,
      ...(this.impresoraActual?.bluetooth || {}),
    };

    /*
     * ============================================================
     * SI YA ESTÁ CONECTADO
     * ============================================================
     */

    if (
      device.gatt.connected &&
      this.bluetoothServer &&
      this.bluetoothCharacteristic
    ) {
      return true;
    }

    console.warn("[ImpresoraManager] Bluetooth desconectado. Reconectando...");

    this.cambiarEstado("reconectando");

    /*
     * ============================================================
     * LIMPIAR REFERENCIAS ANTIGUAS
     * ============================================================
     */

    this.bluetoothServer = null;
    this.bluetoothCharacteristic = null;

    /*
     * ============================================================
     * CONECTAR GATT
     * ============================================================
     */

    const server = await device.gatt.connect();

    this.bluetoothServer = server;

    /*
     * ============================================================
     * BUSCAR NUEVAMENTE EL SERVICIO
     * ============================================================
     */

    const service = await this.buscarServicioBluetooth(
      server,
      bluetoothConfig.serviceUUID,
    );

    /*
     * ============================================================
     * BUSCAR NUEVAMENTE LA CARACTERÍSTICA
     * ============================================================
     */

    const characteristic = await this.buscarCaracteristicaBluetooth(
      service,
      bluetoothConfig.characteristicUUID,
    );

    /*
     * ============================================================
     * ACTUALIZAR UUIDS
     * ============================================================
     */

    bluetoothConfig.serviceUUID = service.uuid;

    bluetoothConfig.characteristicUUID = characteristic.uuid;

    this.configuracion.bluetooth = {
      ...this.configuracion.bluetooth,
      ...bluetoothConfig,
    };

    /*
     * ============================================================
     * GUARDAR NUEVA CARACTERÍSTICA
     * ============================================================
     */

    this.bluetoothCharacteristic = characteristic;

    /*
     * ============================================================
     * ACTUALIZAR IMPRESORA ACTUAL
     * ============================================================
     */

    if (this.impresoraActual) {
      this.impresoraActual.bluetooth = {
        serviceUUID: service.uuid,
        characteristicUUID: characteristic.uuid,
      };

      this.impresoraActual.ultimaConexion = new Date().toISOString();
    }

    /*
     * ============================================================
     * ESTADO
     * ============================================================
     */

    this.impresoraConectada = true;

    this.tipoConexionActual = TIPOS_CONEXION.BLUETOOTH;

    this.cambiarEstado("conectada");

    this.guardarConfiguracion();

    console.log("[ImpresoraManager] Bluetooth reconectado correctamente:", {
      serviceUUID: service.uuid,
      characteristicUUID: characteristic.uuid,
    });

    return true;
  }

  /* ==============================================================
   * LIMPIAR RECURSOS
   * ============================================================== */

  destruir() {
    this.detenerReconectar = true;

    this.detenerReconectarAutomaticamente();

    this.detenerMonitoreo();

    if (this.usbDevice) {
      try {
        if (this.usbDevice.opened) {
          this.usbDevice.close();
        }
      } catch (_) {}
    }

    if (this.serialPort) {
      try {
        this.serialPort.close();
      } catch (_) {}
    }

    if (this.bluetoothDevice?.gatt) {
      try {
        if (this.bluetoothDevice.gatt.connected) {
          this.bluetoothDevice.gatt.disconnect();
        }
      } catch (_) {}
    }

    this.listeners.estado.clear();

    this.listeners.conexion.clear();

    this.listeners.desconexion.clear();

    this.listeners.error.clear();

    this.listeners.impresora.clear();

    this.listeners.impresion.clear();
  }
}

/* ================================================================
 * INSTANCIA ÚNICA
 * ================================================================
 *
 * Puedes importar directamente:
 *
 *   import impresoraManager from "./ImpresoraManager";
 *
 * ================================================================
 */

const impresoraManager = new ImpresoraManager();

export default impresoraManager;

export { ImpresoraManager, TIPOS_CONEXION, TAMANOS_PAPEL };
